    <template>
    <audio ref="audio" controls class="hidden" preload="metadata">
        <source v-if="props?.episode?.audio?.ogg" :src="props?.episode?.audio?.ogg" type="audio/ogg">
        <source v-if="props?.episode?.audio?.mp4" :src="props?.episode?.audio?.mp4" type="audio/mpeg">
        <source v-if="props?.episode?.audio?.mp3" :src="props?.episode?.audio?.mp3" type="audio/mpeg">
        <source v-if="props?.episode?.audio?.aac" :src="props?.episode?.audio?.aac" type="audio/aac">
        Your browser does not support the audio tag.
    </audio>

    <div class="w-full flex justify-center">
        <div class="max-w-md w-full justify-center space-x-10 items-center mb-4 hidden md:flex">
            <div @click="toggleSpeed" class="w-12 text-base font-medium flex items-center justify-center text-xl space-x-0.5 text-gray-700 cursor-pointer hover:text-regal-blue-700 transition-colors duration-100 ease-out">
                <div class="">
                    {{ playbackRate }}
                </div>
                <div class="">
                    ✗
                </div>
            </div>
            <div @click="seekBackward" class="flex items-start font-medium text-xl text-gray-700 cursor-pointer hover:text-regal-blue-700 transition-colors duration-100 ease-out">
                ↺<span class="text-xs">15</span>
            </div>

            <div @click="play" v-if="!playing" :class="[episode.brandColor && brandColorIsLight ? 'text-regal-blue-900' : 'bg-regal-blue-700 hover:bg-regal-blue-800 text-white', 'shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer']" :style="'background-color: ' + episode.brandColor">
                <svg class="fill-current w-7 h-7 -mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 22v-20l18 10-18 10z"/></svg>
            </div>
            <div @click="pause" v-if="playing" :class="[episode.brandColor && brandColorIsLight ? 'text-regal-blue-900' : 'bg-regal-blue-700 hover:bg-regal-blue-800 text-white', 'shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer']" :style="'background-color: ' + episode.brandColor">
                <svg class="fill-current w-12 h-12" viewBox="0 0 512 512"> <g> </g> <path d="M162.642 148.337h56.034v215.317h-56.034v-215.316z"/> <path d="M293.356 148.337h56.002v215.317h-56.002v-215.316z" /></svg>
            </div>

            <div @click="seekForward" class="flex items-start font-medium text-xl text-gray-700 cursor-pointer hover:text-regal-blue-700 transition-colors duration-100 ease-out">
                ↻<span class="text-xs">15</span>
            </div>

            <div class="w-12 text-base font-medium flex items-center justify-center text-xl space-x-0.5 text-gray-700 cursor-pointer hover:text-regal-blue-700 transition-colors duration-100 ease-out">

            </div>
        </div>
    </div>

    <div class="flex w-full items-center space-x-2">
        <div class="md:hidden">
            <div @click="play" v-if="!playing" :class="[episode.brandColor && brandColorIsLight ? 'text-regal-blue-900' : 'bg-regal-blue-700 hover:bg-regal-blue-800 text-white', 'shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer']" :style="'background-color: ' + episode.brandColor">
                <svg class="fill-current w-7 h-7 -mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 22v-20l18 10-18 10z"/></svg>
            </div>
            <div @click="pause" v-if="playing" :class="[episode.brandColor && brandColorIsLight ? 'text-regal-blue-900' : 'bg-regal-blue-700 hover:bg-regal-blue-800 text-white', 'shrink-0 flex items-center justify-center w-16 h-16 rounded-full transition duration-100 ease-out cursor-pointer']" :style="'background-color: ' + episode.brandColor">
                <svg class="fill-current w-12 h-12" viewBox="0 0 512 512"> <g> </g> <path d="M162.642 148.337h56.034v215.317h-56.034v-215.316z"/> <path d="M293.356 148.337h56.002v215.317h-56.002v-215.316z" /></svg>
            </div>
        </div>
        <div class="flex-1 -mt-6 md:mt-0">
            <div class="w-full relative h-20 cursor-pointer" @click="seek">
                <!-- <div class="backdrop-saturate-0 absolute z-20 h-24" :style="'width:' + remaining + '%; left:' + progress + '%'" /> -->
                <SoundWaves :color="episode.brandColor ? episode.brandColor : '#433edb'" :progress="progress" :remaining="remaining"/>
            </div>
            <div class="w-full flex justify-between text-xs font-medium text-gray-800 tracking-tight tabular-nums -mt-1 md:mt-0">
                <div class="">
                    {{ formatTime(currentTime) }}
                </div>
                <div class="">
                    {{ formatTime(duration) }}
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, defineProps, onMounted, defineEmits } from 'vue'
import SoundWaves from '@/Shared/SoundWaves'
import tinycolor from 'tinycolor2'

const props = defineProps({
    episode: Object
})

const emit = defineEmits(['ping'])

const audio = ref(null)
const playing = ref(false)
const playbackRate = ref(1)
const currentStep = ref(null)
const progress = ref(0)
const remaining = ref(100)
const currentTime = ref(0)
const duration = ref(props.episode.duration)
const trackerArray = ref([])
const currentSec = ref(null)
const lastSec = ref(null)
const brandColorIsLight = ref(false)

onMounted(() => {
    let tinyColorBrand = tinycolor(props.episode.brandColor)
    if (tinyColorBrand.isLight()) {
        brandColorIsLight.value = true
    }
    if (props.episode.currentTime) {
        audio.value.currentTime = props.episode.currentTime
        progress.value = (props.episode.currentTime / props.episode.duration) * 100 || 0
        remaining.value = 100 - progress.value
        currentTime.value = props.episode.currentTime
    }
    audio.value.onloadedmetadata = () => {
        duration.value = audio.value.duration
    }
    audio.value.onplay = () => {
        playing.value = true
        currentStep.value = requestAnimationFrame(step)
        if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: props.episode.title,
                artist: props.episode.users[0].name,
                album: props.episode.podcast.title,
                artwork: [
                    { src: props.episode.image, sizes: '96x96',   type: 'image/png' },
                    { src: props.episode.image, sizes: '128x128', type: 'image/png' },
                    { src: props.episode.image, sizes: '192x192', type: 'image/png' },
                    { src: props.episode.image, sizes: '256x256', type: 'image/png' },
                    { src: props.episode.image, sizes: '384x384', type: 'image/png' },
                    { src: props.episode.image, sizes: '512x512', type: 'image/png' },
                ]
            })
        }
    }
    audio.value.onpause = () => {
        playing.value = false
        cancelAnimationFrame(currentStep.value)
    }
    audio.value.onended = () => {
        playing.value = false
        cancelAnimationFrame(currentStep.value)
    }
    audio.value.ontimeupdate = () => {
        currentSec.value = Math.round(audio.value.currentTime)
        if (currentSec.value !== lastSec.value) {
            if (currentSec.value !== trackerArray.value[trackerArray.value.length - 1]) {
                trackerArray.value.push(currentSec.value)
                if (trackerArray.value.length === 5) {
                    emit('ping', trackerArray.value)
                    trackerArray.value = []
                }
            }
        }
        lastSec.value = Math.round(audio.value.currentTime)
    }
})

const play = () => {
    audio.value.play()
}

const pause = () => {
    audio.value.pause()
}

const seek = (e) => {
    var pos = (e.clientX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.getBoundingClientRect().width
    audio.value.currentTime = duration.value * pos
    updateWaves()
}

const toggleSpeed = () => {
    const playbackRates = [.5, .8, 1, 1.2, 1.5, 2, 3];
    const getCurrentSpeed = (el) => el === audio.value.playbackRate;
    const index = playbackRates.findIndex(getCurrentSpeed) == playbackRates.length - 1 ? 0 : playbackRates.findIndex(getCurrentSpeed) + 1;
    playbackRate.value = audio.value.playbackRate = playbackRates[index];
}

const seekBackward = () => {
    audio.value.currentTime = audio.value.currentTime - 15
}

const seekForward = () => {
    audio.value.currentTime = audio.value.currentTime + 15
}

const step = () => {
    updateWaves()
    currentStep.value = requestAnimationFrame(step)
}

const updateWaves = () => {
    currentTime.value = audio.value.currentTime
    progress.value = (audio.value.currentTime / audio.value.duration) * 100 || 0
    remaining.value = 100 - progress.value
}

</script>